// components/Training/TrainingNotification.jsx
// Slide-in drawer shown once per session when the logged-in user has upcoming training
import React, { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { Text, Button } from "@fluentui/react-components";
import {
  CalendarLtr24Regular, Dismiss24Regular, Location24Regular,
  BookOpen24Regular, ArrowRight24Regular, Alert24Regular,
} from "@fluentui/react-icons";
import { useNavigate } from "react-router-dom";
import { useUserAccess } from "../../context/UserAccessContext";
import { getAccessToken } from "../../services/msalService";
import { fetchTrainingRoster, loadLocalRoster } from "../../services/trainingService";

const SESSION_KEY = "lekoil_training_notif_seen";

function fmtDate(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr + "T12:00:00").toLocaleDateString("en-GB", {
    weekday: "short", day: "numeric", month: "short", year: "numeric",
  });
}

function addDays(dateStr, n) {
  const d = new Date(dateStr + "T12:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function daysUntil(dateStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr + "T00:00:00");
  return Math.round((target - today) / 86400000);
}

export default function TrainingNotification() {
  const { instance, accounts } = useMsal();
  const { currentUserEmail } = useUserAccess();
  const navigate = useNavigate();

  const [shown, setShown] = useState(false);   // drawer visible
  const [entries, setEntries] = useState([]);  // user's upcoming sessions

  useEffect(() => {
    if (!currentUserEmail) return;
    if (sessionStorage.getItem(SESSION_KEY) === "1") return;

    const today = new Date().toISOString().slice(0, 10);
    const cutoff = addDays(today, 14);
    const email  = currentUserEmail.toLowerCase();

    function filterMine(list) {
      return list.filter(e =>
        e.handlerEmail?.toLowerCase() === email &&
        e.date >= today &&
        e.date <= cutoff,
      ).sort((a, b) => a.date.localeCompare(b.date));
    }

    // Instant check from localStorage
    const local = filterMine(loadLocalRoster());
    if (local.length > 0) {
      setEntries(local);
      setTimeout(() => setShown(true), 1400);
    }

    // Background SharePoint sync
    (async () => {
      try {
        const token = await getAccessToken(instance, accounts);
        const list  = await fetchTrainingRoster(token);
        const mine  = filterMine(list);
        if (mine.length > 0) {
          setEntries(mine);
          if (!sessionStorage.getItem(SESSION_KEY)) setTimeout(() => setShown(true), 1400);
        }
      } catch {}
    })();
  }, [currentUserEmail, instance, accounts]);

  function dismiss() {
    setShown(false);
    sessionStorage.setItem(SESSION_KEY, "1");
  }

  function goRoster() {
    dismiss();
    navigate("/training-roster");
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Backdrop blur on mobile */}
      {shown && (
        <div
          onClick={dismiss}
          style={{
            position: "fixed", inset: 0, zIndex: 299,
            background: "rgba(0,0,0,0.15)",
            backdropFilter: "blur(1px)",
            // Only show on narrow screens; on desktop the drawer doesn't need a backdrop
            display: "none",
          }}
          className="training-notif-backdrop"
        />
      )}

      {/* Slide-in drawer */}
      <div style={{
        position:   "fixed",
        top:        60,
        right:      0,
        bottom:     0,
        width:      340,
        zIndex:     300,
        display:    "flex",
        flexDirection: "column",
        background: "#fff",
        borderLeft: "1px solid #D5DEFF",
        boxShadow:  "-6px 0 32px rgba(0,26,61,0.14)",
        transform:  shown ? "translateX(0)" : "translateX(110%)",
        transition: "transform 0.38s cubic-bezier(0.34,1.26,0.64,1)",
        pointerEvents: shown ? "auto" : "none",
      }}>
        {/* Header strip */}
        <div style={{
          background: "linear-gradient(135deg, #001A3D 0%, #002D7A 100%)",
          padding: "18px 20px 16px",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CalendarLtr24Regular style={{ color: "#fff", fontSize: 18 }} />
              </div>
              <Text weight="bold" style={{ color: "#fff", fontSize: 14 }}>Your Safety Moment & KSS</Text>
            </div>
            <button
              onClick={dismiss}
              style={{ background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 6, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff" }}
            >
              <Dismiss24Regular style={{ fontSize: 16 }} />
            </button>
          </div>
          <Text style={{ color: "rgba(200,216,255,0.9)", fontSize: 12 }}>
            {(() => {
              const hasToday = entries.some(e => e.date === new Date().toISOString().slice(0, 10));
              const n = entries.length;
              if (hasToday && n === 1) return "You have a session scheduled for today";
              if (hasToday) return `${n} sessions — including one today`;
              return `You have ${n} upcoming session${n !== 1 ? "s" : ""}`;
            })()}
          </Text>
        </div>

        {/* Entry cards */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 16px 0" }}>
          {entries.map((e, i) => {
            const days = daysUntil(e.date);
            const isClose = days <= 2;
            const isToday = days === 0;

            return (
              <div
                key={e.id}
                style={{
                  borderRadius: 12,
                  border: `1.5px solid ${isClose ? "#FFD54F" : "#E0E7FF"}`,
                  background: isClose ? "#FFFBEB" : "#F8FAFF",
                  padding: "14px 16px",
                  marginBottom: 12,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {/* Left accent bar */}
                <div style={{
                  position: "absolute", left: 0, top: 0, bottom: 0, width: 4,
                  background: isClose ? "#FFB300" : "#0066FF",
                  borderRadius: "12px 0 0 12px",
                }} />

                {/* Urgency badge */}
                {isClose && (
                  <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 8 }}>
                    <Alert24Regular style={{ fontSize: 13, color: "#B8860B" }} />
                    <Text style={{ fontSize: 11, fontWeight: 700, color: "#B8860B" }}>
                      {isToday ? "TODAY!" : `In ${days} day${days !== 1 ? "s" : ""}!`}
                    </Text>
                  </div>
                )}

                {/* Date */}
                <Text weight="bold" style={{ color: "#001A3D", fontSize: 14, display: "block", marginBottom: 6 }}>
                  {fmtDate(e.date)}
                  {!isClose && (
                    <span style={{ fontSize: 11, fontWeight: 400, color: "#7A86A0", marginLeft: 8 }}>
                      in {days} day{days !== 1 ? "s" : ""}
                    </span>
                  )}
                </Text>

                {/* Topic */}
                {e.topic && (
                  <div style={{ display: "flex", gap: 6, alignItems: "flex-start", marginBottom: 4 }}>
                    <BookOpen24Regular style={{ fontSize: 13, color: "#0066FF", flexShrink: 0, marginTop: 1 }} />
                    <Text style={{ fontSize: 13, color: "#201F1E", lineHeight: 1.4 }}>{e.topic}</Text>
                  </div>
                )}

                {/* Venue */}
                {e.venue && (
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <Location24Regular style={{ fontSize: 13, color: "#7A86A0", flexShrink: 0 }} />
                    <Text style={{ fontSize: 12, color: "#7A86A0" }}>{e.venue}</Text>
                  </div>
                )}

                {/* Notes preview */}
                {e.notes && (
                  <div style={{ marginTop: 8, padding: "8px 10px", background: "rgba(0,102,255,0.06)", borderRadius: 6 }}>
                    <Text style={{ fontSize: 11, color: "#555", lineHeight: 1.5, display: "block" }}>{e.notes.length > 100 ? e.notes.slice(0, 100) + "…" : e.notes}</Text>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer actions */}
        <div style={{ padding: "14px 16px 20px", borderTop: "1px solid #E0E7FF", flexShrink: 0, display: "flex", gap: 8 }}>
          <Button
            appearance="primary"
            icon={<ArrowRight24Regular />}
            iconPosition="after"
            onClick={goRoster}
            style={{ flex: 1, background: "#001A3D" }}
          >
            View Full Roster
          </Button>
          <Button appearance="outline" onClick={dismiss}>
            Dismiss
          </Button>
        </div>
      </div>
    </>
  );
}
